import React, { useEffect, useState } from 'react';
import { UserRole } from '../types';
import { AlertTriangle, ArrowRight, FileText, X } from 'lucide-react';

export interface BillingHoldResolveContext {
  service_id: string;
  client_id: string;
  first_name: string;
  last_name: string;
  dos: string;
  service_code: string;
  modifier?: string;
  billable_units?: number;
  calculated_charge?: number;
  billing_route?: string;
  provider_name?: string;
  provider_role?: string;
  primary_payer_name?: string;
  readiness_status: string;
  hold_reasons: string[];
  billing_disposition: 'ACTIVE' | 'CLOSED_NON_BILLABLE' | string;
  billing_disposition_reason?: string | null;
  short_visit_approval_required?: boolean;
  documents: Array<{
    id: string;
    doc_type: string;
    title: string;
    status: string;
    created_at?: string | null;
    has_clinician_signature?: boolean;
    has_guardian_signature?: boolean;
    has_supervisor_signature?: boolean;
  }>;
  claims: Array<{
    id: string;
    claim_number: string;
    status: string;
    batch_id?: string | null;
    billed_amount?: number;
    created_at?: string | null;
  }>;
}

interface BillingHoldResolvePanelProps {
  serviceId: string;
  userRole: UserRole;
  onDismiss: () => void;
  onResolved: () => void;
  onOpenDocument: (docId: string) => void;
  onOpenClaims: () => void;
}

export const BillingHoldResolvePanel: React.FC<BillingHoldResolvePanelProps> = ({
  serviceId,
  userRole,
  onDismiss,
  onResolved,
  onOpenDocument,
  onOpenClaims
}) => {
  const isOwner = userRole === 'OWNER';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const [context, setContext] = useState<BillingHoldResolveContext | null>(null);
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'CLOSE' | 'REOPEN' | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
    setError(null);
    setMode(null);
    setReason('');
    setSavedNotice(null);
      try {
        const res = await fetch(`/api/billing/services/${encodeURIComponent(serviceId)}/resolve-context`, {
          credentials: 'include'
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Unable to load this service.');
        }
        if (!cancelled) setContext(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Unable to load this service.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  const isClosed = String(context?.billing_disposition || 'ACTIVE') === 'CLOSED_NON_BILLABLE';
  const blockingClaims = context?.claims || [];
  const canClose = isOwner && !isClosed && blockingClaims.length === 0 && !savedNotice;
  const canReopen = isOwner && isClosed && !savedNotice;
  const canApproveShortVisit = (userRole === 'OWNER' || userRole === 'ADMIN' || userRole === 'OFFICE')
    && Boolean(context?.short_visit_approval_required)
    && !savedNotice;

  const approveShortVisit = async () => {
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const res = await fetch(`/api/billing/services/${encodeURIComponent(serviceId)}/approve-short-visit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'The short visit was not approved.');
      }
      if (data.displayOutOfSync) {
        setSavedNotice(data.warning || 'The operation was saved. Server cache recovery is required before this view can be trusted. Do not retry this action.');
        return;
      }
      setSavedNotice(data.message || 'This short visit is approved for billing. Other holds were left in place.');
      const reload = await fetch(`/api/billing/services/${encodeURIComponent(serviceId)}/resolve-context`, {
        credentials: 'include'
      });
      const reloaded = await reload.json().catch(() => null);
      if (reload.ok && reloaded) setContext(reloaded);
    } catch (err: any) {
      setError(err?.message || 'The short visit was not approved.');
    } finally {
      setSaving(false);
    }
  };

  const submitDisposition = async (action: 'CLOSE_NON_BILLABLE' | 'REOPEN') => {
    const trimmed = reason.trim();
    if (!trimmed) {
      setError('A reason is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSavedNotice(null);
    try {
      const res = await fetch(`/api/billing/services/${encodeURIComponent(serviceId)}/disposition`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason: trimmed })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const claimNote = Array.isArray(data.claims) && data.claims.length > 0
          ? ` Linked claims: ${data.claims.map((c: any) => c.claim_number || c.id).join(', ')}.`
          : '';
        throw new Error((data.error || 'The change was not saved.') + claimNote);
      }
      if (data.runtime_cache_synced === false) {
        setSavedNotice(data.runtime_cache_warning || 'The operation was saved. Server cache recovery is required before this view can be trusted. Do not retry this action.');
        setMode(null);
        return;
      }
      onResolved();
    } catch (err: any) {
      setError(err?.message || 'The change was not saved.');
    } finally {
      setSaving(false);
    }
  };

  const clientName = `${context?.first_name || ''} ${context?.last_name || ''}`.trim() || 'Client';

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200/90 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-5 space-y-4 shadow-xl text-slate-800">
        <div className="flex justify-between items-start gap-3 border-b border-slate-200 pb-3">
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900">Review / Resolve Billing Hold</h3>
            <p className="text-[11px] text-slate-600 mt-0.5">Service-specific hold review. Clinical records are not changed.</p>
          </div>
          <button type="button" onClick={onDismiss} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 cursor-pointer" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <p className="text-xs text-slate-500">Loading service…</p>
        ) : !context ? (
          <p className="text-xs text-rose-700">{error || 'Service not found.'}</p>
        ) : (
          <>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5 text-xs">
              <div className="text-slate-900 font-semibold">{clientName}</div>
              <div className="font-mono text-slate-600 text-[11px] flex flex-wrap gap-x-3 gap-y-1">
                <span>Service: {context.service_id}</span>
                <span>DOS: {context.dos}</span>
                <span>CPT: {context.service_code}{context.modifier ? `-${context.modifier}` : ''}</span>
              </div>
              <div className="text-slate-600 text-[11px]">
                Provider: {context.provider_name || '—'} {context.provider_role ? `(${context.provider_role})` : ''}
                <span className="mx-2 text-slate-300">•</span>
                Payer: {context.primary_payer_name || '—'}
              </div>
              <div className="text-[11px]">
                <span className="text-slate-500">Readiness: </span>
                <span className="font-mono text-slate-800">{context.readiness_status}</span>
                {isClosed && (
                  <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-bold uppercase">
                    Closed / Non-billable
                  </span>
                )}
              </div>
              {isClosed && context.billing_disposition_reason && (
                <div className="text-[12px] text-slate-800">
                  <span className="text-slate-500">Closed / non-billable reason: </span>
                  {context.billing_disposition_reason}
                </div>
              )}
            </div>

            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Hold reasons</div>
              {context.hold_reasons?.length ? (
                <div className="space-y-1">
                  {context.hold_reasons.map((reasonText, idx) => (
                    <div key={idx} className="flex items-start gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0 mt-1" />
                      <span className="text-[12px] text-slate-700">{reasonText}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[12px] text-slate-500">No hold reasons recorded for this service.</p>
              )}
            </div>

            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Linked clinical documents</div>
              {context.documents.length === 0 ? (
                <p className="text-[12px] text-slate-500">No clinical document is linked to this service.</p>
              ) : (
                <div className="space-y-1.5">
                  {context.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                      <div className="min-w-0">
                        <div className="text-[11px] text-slate-800 font-semibold truncate">
                          {doc.doc_type || 'DOCUMENT'}{doc.title ? ` · ${doc.title}` : ''}
                        </div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{doc.id}{doc.status ? ` · ${doc.status}` : ''}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onOpenDocument(doc.id)}
                        className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sky-600 hover:bg-sky-500 text-white text-[10px] font-bold cursor-pointer"
                      >
                        <FileText className="w-3 h-3" />
                        Open
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {blockingClaims.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                <div className="flex items-start gap-2 text-amber-900 text-[12px] font-semibold">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>This service already belongs to a claim and cannot be closed as non-billable.</span>
                </div>
                {blockingClaims.map((claim) => (
                  <div key={claim.id} className="text-[11px] text-slate-700 font-mono">
                    {claim.claim_number} · {claim.status}{claim.batch_id ? ` · batch ${claim.batch_id}` : ''}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={onOpenClaims}
                  className="inline-flex items-center gap-1 text-[11px] text-sky-700 hover:text-sky-800 font-semibold cursor-pointer"
                >
                  <span>Open Claims</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            )}

            {savedNotice && (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                {savedNotice}
              </div>
            )}
            {error && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-800">
                {error}
              </div>
            )}

            {mode && (
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {mode === 'CLOSE' ? 'Reason to close as non-billable' : 'Reason to reopen'}
                </label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  className="w-full rounded-md border border-slate-300 bg-white text-slate-900 text-xs p-2 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-sky-500 focus:border-sky-500"
                  placeholder="Required reason"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center justify-end gap-2 pt-1 border-t border-slate-200">
              <button
                type="button"
                disabled={saving}
                onClick={onDismiss}
                className="px-3 py-1.5 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold cursor-pointer border border-slate-200"
              >
                Keep on hold
              </button>
              {canApproveShortVisit && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => { void approveShortVisit(); }}
                  className="px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold cursor-pointer disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Approve short visit for billing'}
                </button>
              )}
              {canClose && mode !== 'CLOSE' && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => { setError(null); setMode('CLOSE'); }}
                  className="px-3 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer"
                >
                  Close as non-billable
                </button>
              )}
              {canClose && mode === 'CLOSE' && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => submitDisposition('CLOSE_NON_BILLABLE')}
                  className="px-3 py-1.5 rounded-lg bg-rose-700 hover:bg-rose-600 text-white text-xs font-bold cursor-pointer disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Confirm close'}
                </button>
              )}
              {canReopen && mode !== 'REOPEN' && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => { setError(null); setMode('REOPEN'); }}
                className="px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-xs font-bold cursor-pointer"
                >
                  Reopen
                </button>
              )}
              {canReopen && mode === 'REOPEN' && (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => submitDisposition('REOPEN')}
                  className="px-3 py-1.5 rounded-lg bg-sky-700 hover:bg-sky-600 text-white text-xs font-bold cursor-pointer disabled:opacity-60"
                >
                  {saving ? 'Saving…' : 'Confirm reopen'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
